import { useRef } from 'react';
import { Plus, Settings } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { FunnelStepColumn } from './FunnelStepColumn';
import { FlowStep, Storefront } from '../types';
import { getStepsByColumn } from '../utils/helpers';

interface GridEditorProps {
  funnel: Storefront;
  validSteps: FlowStep[];
  selectedStepId: string | null;
  onStepSelect: (id: string | null) => void;
  onFunnelChange: (funnel: Storefront) => void;
  onStepDelete: (stepId: string) => void;
  onAddConditionalStep: (col: number) => void;
  onAddColumn: (insertAfterCol?: number) => void;
  onTransformToSplit: (stepId: string) => void;
  onEditConfig: (stepId: string) => void;
  urlViewMode: 'staging' | 'production';
  stepAnalytics?: Record<
    string,
    {
    visitors: number;
    conversions: number;
      progressionRate: number | null;
      progressedToNext: number;
    realtimeVisitors: number;
      conversionValue: number;
      conversionValueUnit: string;
      avgConversionValue: number;
      hasNextStep: boolean;
    }
  >;
  variantAnalytics?: Record<
    string,
    {
    visitors: number;
    conversions: number;
      progressionRate: number | null;
      progressedToNext: number;
    realtimeVisitors: number;
      conversionValue: number;
      conversionValueUnit: string;
      avgConversionValue: number;
      hasNextStep: boolean;
    }
  >;
}

/**
 * Grid-based funnel editor
 * Horizontal column layout with conditional alternatives
 */
export function GridEditor({
  funnel,
  validSteps,
  selectedStepId,
  onStepSelect,
  onFunnelChange,
  onStepDelete,
  onAddConditionalStep,
  onAddColumn,
  onTransformToSplit,
  onEditConfig,
  urlViewMode,
  stepAnalytics,
  variantAnalytics,
}: GridEditorProps) {
  const gridContainerRef = useRef<HTMLDivElement>(null);
  const stepRefsMap = useRef<Map<string, HTMLDivElement>>(new Map());

  const stepsByColumn = getStepsByColumn(validSteps);
  
  // Calculate max column with safety checks
  const maxCol =
    validSteps.length > 0
      ? Math.max(
          ...validSteps.map((s) => {
        const col = s.position?.col ?? 0;
        // Ensure col is a valid number and not too large
        return typeof col === 'number' && isFinite(col) && col >= 0 && col < 1000 ? col : 0;
          }),
          -1,
        )
    : -1;
  const numCols = Math.max(0, Math.min(maxCol + 1, 100)); // Cap at 100 columns max

  return (
    <div
      ref={gridContainerRef}
      className="h-full w-full overflow-x-auto overflow-y-auto bg-gradient-to-br from-slate-50 via-white to-slate-50 px-8 py-8 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950"
      onClick={(e) => {
        // Only process if there's a selected step to close
        if (!selectedStepId) return;

        const target = e.target as HTMLElement;

        // Check if click is inside step editor (shouldn't happen due to stopPropagation, but safety check)
        const clickedOnStepEditor = target.closest('[data-step-editor]');
        if (clickedOnStepEditor) return;

        const clickedOnStepBox = target.closest('[data-step-box]');
        const clickedOnButton = target.closest('button');
        const clickedOnInput = target.closest('input, select, textarea');
        const clickedOnPopover = target.closest('[role="dialog"], [role="listbox"], [role="combobox"]');
        const clickedOnPageSelector = target.closest('[data-page-selector]');

        if (
          !clickedOnStepBox &&
          !clickedOnButton &&
          !clickedOnInput &&
          !clickedOnPopover &&
          !clickedOnPageSelector
        ) {
          onStepSelect(null);
        }
      }}
    >
      <div className="min-w-max">
        <div className="space-y-6">
          {/* Empty State - Large and Friendly */}
          {(!funnel.steps || funnel.steps.length === 0 || validSteps.length === 0) && (
            <div className="flex min-h-[500px] items-center justify-center">
              <Card className="w-full max-w-2xl border-2 border-dashed border-slate-300 shadow-xl dark:border-slate-700">
                <CardHeader className="space-y-2 text-center">
                  <div className="bg-primary shadow-primary/30 mx-auto flex h-16 w-16 items-center justify-center rounded-full shadow-lg">
                    <Settings className="text-primary-foreground h-8 w-8" />
                  </div>
                  <CardTitle className="text-2xl font-bold">Build Your Sales Funnel</CardTitle>
                  <CardDescription className="text-base">
                    Create your first page to start building your customer journey
                  </CardDescription>
                </CardHeader>
                <CardContent className="flex justify-center pb-8">
                  <Button
                    onClick={() => onAddColumn()}
                    size="lg"
                    className="bg-primary shadow-primary/30 hover:bg-primary/90 hover:shadow-primary/40 h-12 gap-2 px-6 text-base font-medium shadow-lg transition-all hover:shadow-xl"
                  >
                    <Plus className="h-5 w-5" />
                    Add Your First Page
                  </Button>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Funnel Flow - Horizontal Layout */}
          {numCols > 0 && validSteps.length > 0 && (
            <div className="flex gap-6">
              {/* Add Page button at the BEGINNING */}
              <button
                onClick={() => onAddColumn(-1)}
                className="group/add hover:border-primary/50 hover:bg-primary/5 dark:hover:border-primary/50 dark:hover:bg-primary/10 flex w-16 flex-shrink-0 cursor-pointer items-center justify-center self-stretch rounded-2xl border-2 border-dashed border-slate-300 bg-white/50 shadow-sm transition-all hover:scale-105 dark:border-slate-700 dark:bg-slate-900/50"
                title="Add page at the beginning"
              >
                <Plus className="group-hover/add:text-primary dark:group-hover/add:text-primary h-7 w-7 text-slate-400 transition-all group-hover/add:scale-110 dark:text-slate-600" />
              </button>

              {Array.from({ length: numCols }).map((_, colIndex) => {
                const stepsInCol = stepsByColumn[colIndex] || [];

                return (
                  <div key={colIndex} className="flex gap-6">
                    <FunnelStepColumn
                      colIndex={colIndex}
                      stepsInCol={stepsInCol}
                      funnel={{ ...funnel, steps: validSteps }}
                      onFunnelChange={onFunnelChange}
                      selectedStepId={selectedStepId}
                      onStepSelect={onStepSelect}
                      onStepDelete={onStepDelete}
                      onAddConditionalStep={onAddConditionalStep}
                      onTransformToSplit={onTransformToSplit}
                      onEditConfig={onEditConfig}
                      stepRefsMap={stepRefsMap}
                      urlViewMode={urlViewMode}
                      stepAnalytics={stepAnalytics}
                      variantAnalytics={variantAnalytics}
                    />

                    {/* Add Page button - Vertical button between pages */}
                    <button
                      onClick={() => onAddColumn(colIndex)}
                      className="group/add hover:border-primary/50 hover:bg-primary/5 dark:hover:border-primary/50 dark:hover:bg-primary/10 flex w-16 flex-shrink-0 cursor-pointer items-center justify-center self-stretch rounded-2xl border-2 border-dashed border-slate-300 bg-white/50 shadow-sm transition-all hover:scale-105 dark:border-slate-700 dark:bg-slate-900/50"
                      title={`Add page after Page ${colIndex + 1}`}
                    >
                      <Plus className="group-hover/add:text-primary dark:group-hover/add:text-primary h-7 w-7 text-slate-400 transition-all group-hover/add:scale-110 dark:text-slate-600" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
